import { useQuery } from '@tanstack/react-query';
import { formatDateAndTime } from '@/utils/formatDate';

import { useEffect, useMemo, useState } from 'react';
import { ShipPageData } from '@/types/dashboard/ShipPage';
import { getArmadoresList } from '@/services/getListArmadores';
import {
  getDashboardShipPageViajesData,
  getDashboardShipPageData,
} from '@/services/dashboardShipPage';
import useAuthContext from '../useAuthContext';

export const useShipPage = () => {
  const { userDataDecoded } = useAuthContext();
  const [shipPageData, setShipPageData] = useState<ShipPageData>();
  const [loading, setLoading] = useState(true);

  // Obtener la lista de armadores
  const { data: armadoresList, isLoading: loadingArmadores } = useQuery({
    queryKey: ['armadoresListDropdown'],
    queryFn: getArmadoresList,
  });

  const armadoresListForDropdown = useMemo(() => {
    const options = (
      userDataDecoded?.armadoresDashboard || userDataDecoded?.armadores
    )
      ?.split(';')
      .map((val) => {
        return { value: val, name: val };
      });
    const shipownerOptions = armadoresList?.map(
      (val: { codigo: string; nombre: string }) => {
        return { value: val.codigo, name: val.nombre };
      }
    );
    return userDataDecoded?.armadores === '*' ? shipownerOptions : options;
  }, [userDataDecoded, armadoresList]);

  const [currentArmador, setCurrentArmador] = useState({ value: '', name: '' });

  const { data: viajesList, isLoading: loadingViajes } = useQuery({
    queryKey: ['viajesListDropdown', currentArmador.value],
    queryFn: () =>
      getDashboardShipPageViajesData({ armador: currentArmador.value }),
    enabled: currentArmador.value !== '',
    initialData: [],
  });

  const viajesListForDropdown = useMemo(() => {
    return viajesList
      ?.sort(
        (a, b) => new Date(b.llegada).getTime() - new Date(a.llegada).getTime()
      )
      ?.map((val: { codigo: string; buque: string; llegada: string }) => {
        return {
          value: val.codigo,
          label: `${val.buque.toUpperCase()}  - ${formatDateAndTime(val.llegada).toUpperCase()}`,
        };
      });
  }, [viajesList]);

  const [currentViaje, setCurrentViaje] = useState({ value: '', label: '' });

  // Obtener los datos de la página basado en el viaje actual
  const { data: pageData, isLoading: loadingPageData } = useQuery({
    queryKey: ['shipPageData', currentViaje.value, currentArmador.value], // Asegurar que currentViaje.value es parte de la queryKey
    queryFn: () =>
      getDashboardShipPageData({
        viaje: currentViaje.value,
        armador: currentArmador.value,
      }),
    enabled: currentViaje.value !== '' && currentArmador.value !== '',
    initialData: {} as ShipPageData,
  });

  useEffect(() => {
    if (pageData) {
      setShipPageData(pageData);
    }
  }, [pageData]);

  useEffect(() => {
    if (!loadingArmadores && !loadingViajes && !loadingPageData && pageData) {
      setLoading(false);
    }
  }, [loadingArmadores, loadingViajes, loadingPageData, pageData]);

  useEffect(() => {
    // Reset currentViaje when currentArmador changes
    if (viajesListForDropdown && viajesListForDropdown.length > 0) {
      setCurrentViaje(viajesListForDropdown[0]);
    } else {
      setCurrentViaje({ value: '', label: '' });
    }
  }, [viajesListForDropdown, currentArmador]);

  return {
    armadoresListForDropdown,
    currentArmador,
    setCurrentArmador,
    viajesListForDropdown,
    currentViaje,
    setCurrentViaje,
    shipPageData,
    viajesList,
    pageData,
    loading,
    loadingArmadores,
    loadingViajes,
    loadingPageData,
  };
};
